// Supabase's client fires a SIGNED_OUT event automatically when a session
// becomes invalid out from under it (deleted account, revoked refresh token,
// etc) - without this, pages that assumed a logged-in user would silently
// render blank instead of telling the person what happened. A deliberate
// "Logg ut" click already clears pws_auth BEFORE calling signOut(), so this
// only fires for the involuntary case (pws_auth still '1' when SIGNED_OUT lands).
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiUserX } from "react-icons/fi";
import { supabase } from "../../supabase";
import "./SystemState.css";

const SKIP_PATHS = ["/LogIn", "/CreateUser", "/forgot-password", "/auth-action"];

export default function SessionExpiredGate() {
  const { t } = useTranslation('systemState');
  const [expired, setExpired] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const flagExpired = () => {
    // Every page caches its own slice of data under its own key
    // (pws_user_cache, pws_coupons_cache, cartItems, orders, ...) with no
    // shared prefix - hand-maintaining that list here always drifts out of
    // sync as new caches get added. A dead account means none of it is
    // trustworthy anymore, so just wipe everything instead of guessing.
    localStorage.clear();
    sessionStorage.clear();
    setExpired(true);
  };

  useEffect(() => {
    // Live case: an already-running tab's session goes invalid mid-session
    // (account deleted while browsing, refresh token revoked, etc).
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN") {
        // A genuine fresh sign-in proves the account is real again - clear
        // any stale "expired" state left over from before this login (e.g.
        // the cold-load check below ran against an old dead session before
        // the person typed in fresh, valid credentials).
        setExpired(false);
        return;
      }
      if (event !== "SIGNED_OUT") return;
      const wasLoggedIn = localStorage.getItem("pws_auth") === "1";
      if (!wasLoggedIn) return; // deliberate logout already cleared this flag first
      flagExpired();
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    // Cold-load case: a fresh page load only ever reports the LOCAL cached
    // session (INITIAL_SESSION), never SIGNED_OUT - if that account was
    // deleted server-side, every page that trusts its own localStorage cache
    // (Home's points/coupons card, etc) would otherwise keep rendering stale
    // ghost data forever. getUser() forces a real server round-trip.
    if (localStorage.getItem("pws_auth") !== "1") return;
    supabase.auth.getUser().then(({ data, error }) => {
      if (error || !data?.user) flagExpired();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!expired || SKIP_PATHS.includes(location.pathname)) return null;

  const goToLogin = () => {
    setExpired(false);
    navigate("/LogIn", { replace: true });
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "#0B1437" }}>
      <div className="system-state">
        <div className="system-state-icon-badge system-state-icon-badge--red">
          <FiUserX size={24} />
        </div>
        <h2 className="system-state-title">{t('loggedOutTitle')}</h2>
        <p className="system-state-desc">{t('loggedOutDesc')}</p>
        <div className="system-state-actions">
          <button className="system-state-btn-primary" onClick={goToLogin}>
            {t('goToLogin')}
          </button>
        </div>
      </div>
    </div>
  );
}
